import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { TrendingUp, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MaskedLines, Reveal } from "../components/Motion";
import { CTASection } from "../components/CTASection";
import { Seo } from "../components/Seo";
import { trackEvent } from "../components/Analytics";
import { postRentalAnalysis } from "../lib/api";

const cities = ["Boston", "Cambridge", "Somerville", "Brookline", "Newton", "Quincy", "Worcester", "Springfield", "Lowell", "Framingham", "Waltham", "Medford", "Other"];
const types = [
  { v: "single_family", l: "Single Family" },
  { v: "condo", l: "Condo" },
  { v: "multi_family", l: "Multi-Family" },
  { v: "townhouse", l: "Townhouse" },
  { v: "apartment", l: "Apartment" },
];
const conditions = [
  { v: "excellent", l: "Excellent — recently renovated" },
  { v: "good", l: "Good — well maintained" },
  { v: "fair", l: "Fair — some updates needed" },
  { v: "needs_work", l: "Needs Work" },
];

const field = "w-full border border-border bg-background px-4 py-3.5 text-sm focus:border-accent focus:outline-none";

export default function RentalAnalysis() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", city: "", property_type: "",
    bedrooms: "", bathrooms: "", square_feet: "", condition: "good",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.property_type) { toast.error("Please select a city and property type."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        bedrooms: parseInt(form.bedrooms || 0, 10),
        bathrooms: parseFloat(form.bathrooms || 0),
        square_feet: parseInt(form.square_feet || 0, 10),
      };
      const data = await postRentalAnalysis(payload);
      setResult(data);
      trackEvent("rental_analysis", { city: form.city, property_type: form.property_type, result: data.status === "manual" ? "manual" : "instant" });
      if (data.status === "manual") {
        toast.success("Request received — our team will email your analysis shortly.");
      } else {
        toast.success("Estimate ready — full report sent to your email.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="What's My Rental Worth?" description="Free instant rental estimate for your Massachusetts property. Get an expert-backed monthly rent range in seconds." />

      <section className="border-b border-border pt-36 pb-14 lg:pt-44 lg:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal><span className="overline text-accent">Rental Analysis Tool</span></Reveal>
          <MaskedLines
            el="h1"
            lines={["How much is", "your rental worth?"]}
            delay={0.1}
            className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl"
          />
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl text-muted-foreground sm:text-lg">
              Get an instant, data-informed monthly rent estimate for your Massachusetts property — plus a detailed report from our team.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] gap-px border-x border-border bg-border lg:grid-cols-[1.2fr_1fr]">
          {/* FORM */}
          <div className="bg-background p-8 lg:p-14">
            <form onSubmit={submit} className="space-y-8" data-testid="rental-analysis-form">
              <div>
                <span className="overline text-accent">Property details</span>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <input className={field + " sm:col-span-2"} placeholder="Street address" required
                    data-testid="ra-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
                  <Select value={form.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger className="rounded-none border-border py-6" data-testid="ra-city"><SelectValue placeholder="City / Town" /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                    <SelectTrigger className="rounded-none border-border py-6" data-testid="ra-type"><SelectValue placeholder="Property type" /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <input className={field} type="number" min="0" placeholder="Bedrooms" required
                    data-testid="ra-bedrooms" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} />
                  <input className={field} type="number" min="0" step="0.5" placeholder="Bathrooms" required
                    data-testid="ra-bathrooms" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} />
                  <input className={field} type="number" min="0" placeholder="Square feet" required
                    data-testid="ra-sqft" value={form.square_feet} onChange={(e) => set("square_feet", e.target.value)} />
                  <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                    <SelectTrigger className="rounded-none border-border py-6" data-testid="ra-condition"><SelectValue placeholder="Condition" /></SelectTrigger>
                    <SelectContent>{conditions.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <span className="overline text-accent">Where to send your report</span>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <input className={field} placeholder="Full name" required data-testid="ra-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <input className={field} type="email" placeholder="Email" required data-testid="ra-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <input className={field + " sm:col-span-2"} placeholder="Phone (optional)" data-testid="ra-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="ra-submit"
                className="group flex w-full items-center justify-center gap-2 bg-accent px-6 py-4 text-sm font-medium text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
                <TrendingUp className="h-4 w-4" />
                {loading ? "Calculating…" : "Calculate My Rental Value"}
              </button>
            </form>
          </div>

          {/* RESULT */}
          <div className="flex flex-col justify-center bg-primary p-8 text-primary-foreground lg:p-14">
            <AnimatePresence mode="wait">
              {result && result.status === "manual" ? (
                <motion.div key="manual" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="ra-manual">
                  <span className="overline text-accent">Manual analysis requested</span>
                  <p className="mt-4 font-serif text-4xl font-light leading-tight lg:text-5xl">
                    Our team will email your analysis.
                  </p>
                  <p className="mt-6 text-sm text-primary-foreground/70">{result.message}</p>
                  <button onClick={() => setResult(null)} className="mt-8 flex items-center gap-2 text-sm text-accent hover:underline" data-testid="ra-reset">
                    <RefreshCw className="h-3.5 w-3.5" /> Try another property
                  </button>
                </motion.div>
              ) : result ? (
                <motion.div key="result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="ra-result">
                  <span className="overline text-accent">Estimated monthly rent</span>
                  <div className="mt-4 font-serif text-6xl font-light lg:text-7xl">${result.estimate.toLocaleString()}</div>
                  <div className="mt-3 text-primary-foreground/70">Expected range: ${result.low.toLocaleString()} – ${result.high.toLocaleString()}</div>
                  <div className="mt-8 space-y-4 border-t border-primary-foreground/15 pt-8">
                    <div className="flex justify-between"><span className="text-primary-foreground/60">Annual gross</span><span className="font-medium">${result.annual_gross.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-primary-foreground/60">Price / sq ft</span><span className="font-medium">${result.price_per_sqft}</span></div>
                    {typeof result.comparables === "number" && (
                      <div className="flex justify-between"><span className="text-primary-foreground/60">Comparables used</span><span className="font-medium">{result.comparables}</span></div>
                    )}
                  </div>
                  <p className="mt-8 text-sm text-primary-foreground/60">Based on live market data{result.source ? ` (${result.source})` : ""}. A detailed report is on its way to your inbox — our team will follow up with a precise valuation.</p>
                  <button onClick={() => setResult(null)} className="mt-6 flex items-center gap-2 text-sm text-accent hover:underline" data-testid="ra-reset">
                    <RefreshCw className="h-3.5 w-3.5" /> Run another estimate
                  </button>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <span className="overline text-accent">Your estimate</span>
                  <p className="mt-4 font-serif text-3xl font-light leading-tight text-primary-foreground/80 lg:text-4xl">
                    Fill in your property details and we'll calculate an instant rent estimate.
                  </p>
                  <p className="mt-6 text-sm text-primary-foreground/50">
                    Backed by 10+ years of Massachusetts market data. Free, instant, no obligation.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
